import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip, Cell,
} from 'recharts';
import { BarChart2, RefreshCw, TrendingUp, Package, ShoppingCart, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtINR(n) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);
}
function fmtINRFull(n) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0);
}
function shortenName(name, max = 18) {
  return name.length > max ? name.slice(0, max - 1) + '…' : name;
}

const METRICS = [
  { key: 'total_revenue', label: 'Revenue',      icon: TrendingUp,    fmt: fmtINR,            unit: '₹' },
  { key: 'total_kg',      label: 'Volume (KG)',  icon: Package,       fmt: n => `${n.toFixed(0)} kg`, unit: 'kg' },
  { key: 'sales_count',   label: 'No. of Sales', icon: ShoppingCart,  fmt: n => String(n),     unit: '' },
];

const ChartTip = ({ active, payload, label, metricFmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black text-white text-xs px-3 py-2 rounded shadow-lg max-w-[200px]">
      <p className="font-semibold mb-0.5 truncate">{label}</p>
      <p>{metricFmt(payload[0].value)}</p>
    </div>
  );
};

export function CustomerPerformance() {
  const { authHeader } = useAuth();
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric,  setMetric]  = useState('total_revenue');

  useEffect(() => {
    axios.get(`${API}/analytics/customers?limit=10`, { headers: authHeader() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const metricCfg  = METRICS.find(m => m.key === metric);
  const sorted     = [...data].sort((a, b) => b[metric] - a[metric]);
  const chartData  = sorted.map(d => ({ ...d, name: shortenName(d.customer_name) }));
  const maxVal     = sorted[0]?.[metric] || 1;

  // Summary totals
  const totalRevenue = data.reduce((s, d) => s + d.total_revenue, 0);
  const totalKg      = data.reduce((s, d) => s + d.total_kg, 0);
  const totalSales   = data.reduce((s, d) => s + d.sales_count, 0);

  return (
    <div className="px-6 py-6">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5">
            <BarChart2 size={11} /> Performance
          </div>
          <h1 className="font-black text-3xl tracking-tight">Customer Performance</h1>
          <p className="text-sm text-gray-500 mt-1">Top 10 customers — all time</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Revenue (Top 10)', value: fmtINRFull(totalRevenue), icon: TrendingUp,   color: 'text-gray-900' },
          { label: 'Total Volume (Top 10)',  value: `${totalKg.toFixed(0)} kg`, icon: Package,  color: 'text-gray-900' },
          { label: 'Total Sales (Top 10)',   value: totalSales,                 icon: ShoppingCart, color: 'text-gray-900' },
          { label: 'Unique Customers',       value: data.length,                icon: Users,     color: 'text-gray-900' },
        ].map(s => (
          <div key={s.label} className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-center gap-1.5 mb-2">
              <s.icon size={12} className="text-gray-400" />
              <div className="small-label text-gray-400">{s.label}</div>
            </div>
            <div className={`font-black text-xl tracking-tight ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Metric toggle */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        {METRICS.map(m => (
          <button key={m.key} onClick={() => setMetric(m.key)}
            className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all ${
              metric === m.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="small-label text-gray-400 mb-1">Top 10 by {metricCfg.label}</div>
          <div className="font-black text-[15px] tracking-tight mb-4">Customer Ranking</div>
          {loading ? (
            <div className="flex justify-center py-16"><RefreshCw size={18} className="animate-spin text-gray-400" /></div>
          ) : chartData.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-[13px]">No sales data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(chartData.length * 42, 200)}>
              <BarChart layout="vertical" data={chartData} margin={{ top: 0, right: 60, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="" horizontal={false} stroke="#f3f4f6" />
                <XAxis type="number" hide />
                <YAxis
                  type="category" dataKey="name" width={130}
                  tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false}
                />
                <Tooltip
                  content={(props) => <ChartTip {...props} metricFmt={metricCfg.fmt} />}
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                />
                <Bar dataKey={metric} radius={[0, 4, 4, 0]} maxBarSize={28} label={{ position: 'right', fontSize: 11, fill: '#6b7280', formatter: metricCfg.fmt }}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? '#111' : i < 3 ? '#374151' : '#9ca3af'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Detail table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div className="small-label text-gray-500">Detailed Breakdown</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-2.5 text-left small-label text-gray-400 w-6">#</th>
                  <th className="px-4 py-2.5 text-left small-label text-gray-400">Customer</th>
                  <th className="px-4 py-2.5 text-right small-label text-gray-400">Revenue</th>
                  <th className="px-4 py-2.5 text-right small-label text-gray-400">KG</th>
                  <th className="px-4 py-2.5 text-right small-label text-gray-400">Sales</th>
                  <th className="px-4 py-2.5 text-left small-label text-gray-400">Last Sale</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-[13px]">No data yet.</td></tr>
                ) : sorted.map((r, i) => (
                  <tr key={r.customer_id || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`text-[12px] font-bold ${i === 0 ? 'text-yellow-500' : i === 1 ? 'text-gray-400' : i === 2 ? 'text-amber-600' : 'text-gray-300'}`}>
                        #{i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[13px] font-medium text-gray-900">{r.customer_name}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-[13px] font-semibold text-gray-900">{fmtINRFull(r.total_revenue)}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-[13px] text-gray-600">{r.total_kg.toFixed(0)}</td>
                    <td className="px-4 py-3 text-right text-[13px] text-gray-600">{r.sales_count}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-400">{r.last_sale || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{data.length} customers</div>
        </div>
      </div>
    </div>
  );
}
